import React, { useEffect, useState, useCallback } from "react";
import { Box, Button } from "@mui/material";
import {
  IoIosArrowDroprightCircle,
  IoIosArrowDropleftCircle,
} from "react-icons/io";
import imageCompression from "browser-image-compression";
import RedButton from "../buttons/redButton";
import StepHeader from "./StepHeader";
import { tokens } from "../../theme";
import ClientPicker from "./ClientPicker";
import OrderChecklist from "./OrderChecklist";
import WorkDescription from "./workDescription";
import Photos from "./photos";
import Signature from "./signature";
import { v4 as uuidv4 } from "uuid";
import supabase from "../../supabase";
import { ErrorModal } from "../ModalAlert";
import { useNavigate, useLocation } from "react-router";
import { LoaderSpinner } from "../spinner/loader";
import { FullScreenLoader } from "../spinner/FullScreenLoader";
import { colors } from "../../constants/colors";
import {
  orderObjectStatus,
  isOrderVisibleToOperator,
} from "../../constants/orderStatus";
// import { ivaValues } from "../../constants/paymentValues";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useSelector } from "react-redux";
import { 
  validateImageFile, 
  compressImageWithRetry, 
  getCompressionConfig,
  generateUniqueFileName,
  handleImageError 
} from "../../utils/imageUtils";
import Swal from 'sweetalert2';

const ServiceOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isAdmin = useSelector((state) => state.auth.isAdmin);
  const isAdminStorage = sessionStorage.getItem("isAdmin") === "true";
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isEdition, setIsEdition] = useState(false);
  const [isOrderClosed, setIsOrderClosed] = useState(false);
  const [prevOrder, setPrevOrder] = useState({});
  const [deletedPictures, setDeletedPictures] = useState([]);
  const [formState, setFormState] = useState({
    client: {},
    description: "",
    signature: null,
    signature_aclaration: "",
    pictures: [],
  });
  const [message, setMessage] = useState("");
  const [isFinished, setIsFinished] = useState(false);
  // Estados para controlar botones en móvil en el paso 2
  const [showActionButtons, setShowActionButtons] = useState(false);
  const [initialPicturesCount, setInitialPicturesCount] = useState(0);
  const [initialPictures, setInitialPictures] = useState([]);
  // const [tempDeletedPictures, setTempDeletedPictures] = useState([]);
  // Estado para controlar cuando se están subiendo imágenes
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [isOfflineMode, setIsOfflineMode] = useState(false); // Para rastrear si está en modo sin conexión
  const [forceUpdate, setForceUpdate] = useState(0); // Para forzar re-renderizado
  const { maxWidth, quality } = getCompressionConfig();

  // Función para verificar conexión a Supabase (simplificada)
  const checkConnection = async () => {
    try {
      const { data, error } = await supabase
        .from("employees")
        .select("employeeId")
        .limit(1);
      
      if (error) {
        throw new Error(`Error de conexión: ${error.message}`);
      }
      
      return true;
    } catch (error) {
      console.error("Error de conexión:", error);
      return false;
    }
  };

  // Determinar si el usuario es operador (no admin)
  const isOperator = !(isAdmin || isAdminStorage);
  
  // Determinar si la orden está terminada
  const isOrderTerminated = prevOrder.status === orderObjectStatus.terminado;
  
  // Determinar si debe estar en modo de solo lectura
  const isReadOnlyMode = isOperator && isOrderTerminated;

  const fetchOrderData = useCallback(async (order) => {
    if (isOperator && !isOrderVisibleToOperator(order.status)) {
      ErrorModal({
        title: "Acceso no permitido",
        text: "No tienes permiso para ver órdenes en estado Terminada o finalizada.",
      });
      navigate("/");
      return;
    }

    setIsLoading(true);
    setIsEdition(true);
    setPrevOrder(order);
    
    // Establecer isOrderClosed basado SOLO en el estado de la orden
    // Las órdenes PENDIENTES con firma pueden seguir editándose para agregar imágenes
    if (order.status === orderObjectStatus.terminado) {
      setIsOrderClosed(true);
    }
    
    // Limpiar localStorage para evitar usar datos corruptos
    localStorage.removeItem("signatureData");
    
    // Guardar firma en localStorage solo si no es una imagen de 1x1 píxel
    if (order.clientSignaturePhoto && !order.clientSignaturePhoto.includes('iVBORw0KGgoAAAANSUhEUgAAAAEAAAA')) {
      localStorage.setItem("signatureData", order.clientSignaturePhoto);
    }
    
    
    try {
      // Verificar y obtener datos del cliente si no están completos
      let clientData = order.client;
      
      // Determinar el clientId a usar
      let clientIdToUse = order.clientId || clientData?.clientId;
      
      // Si no tenemos clientId, intentar obtenerlo del objeto client
      if (!clientIdToUse && clientData) {
        // Si el objeto client tiene un clientId, usarlo
        if (clientData.clientId) {
          clientIdToUse = clientData.clientId;
        } else {
          // Si no, intentar buscar el cliente por nombre y empresa
          const { data: clientSearch, error: searchError } = await supabase
            .from("clients")
            .select("clientId")
            .eq("name", clientData.name)
            .eq("company", clientData.company)
            .single();
            
          if (!searchError && clientSearch) {
            clientIdToUse = clientSearch.clientId;
          }
        }
      }
      
      // Si aún no tenemos clientId, no podemos continuar
      if (!clientIdToUse) {
        throw new Error("No se encontró el ID del cliente en la orden. La orden puede estar corrupta.");
      }
      
      // Si el cliente no está completo o no tiene clientId, obtenerlo de la base de datos
      if (!clientData || !clientData.clientId || !clientData.name) {
        const { data: clientFromDb, error: clientError } = await supabase
          .from("clients")
          .select("*")
          .eq("clientId", clientIdToUse)
          .single();
          
        if (clientError) {
          throw new Error("Error al obtener los datos del cliente: " + clientError.message);
        }
        
        if (!clientFromDb) {
          throw new Error("No se encontró el cliente asociado a esta orden");
        }
        
        clientData = clientFromDb;
      }

      // Verificar que el cliente tenga los campos necesarios
      if (!clientData.clientId) {
        throw new Error("Datos del cliente incompletos: falta clientId");
      }

      /*
        Consulta directa para la firma y para los detalles de la orden.

        Los detalles se piden acá y no se confía en que los traiga quien navegó:
        cada listado arma su propio `select` y basta que uno se olvide para que
        la descripción llegue vacía y el guardado falle. Pasaba al abrir una
        orden desde el listado del operador.
      */
      const { data: orderFromDb, error: orderDbError } = await supabase
        .from("orders")
        .select("orderId, status, clientSignaturePhoto, clientSignatureName, orderDetails(*)")
        .eq("orderId", order.orderId)
        .single();
        
      if (orderDbError) {
        console.error("Error en consulta directa:", orderDbError);
      }

      if (
        isOperator &&
        orderFromDb?.status != null &&
        !isOrderVisibleToOperator(orderFromDb.status)
      ) {
        ErrorModal({
          title: "Acceso no permitido",
          text: "No tienes permiso para ver órdenes en estado Terminada o finalizada.",
        });
        navigate("/");
        return;
      }

      // Obtener archivos del storage
      const { data } = await supabase.storage.from(order.bucketId).list(`${order.orderId}/`);
      const files = await Promise.all(
        data.map(async (file) => {
          const { data } = supabase.storage
            .from(order.bucketId)
            .getPublicUrl(`${order.orderId}/${file.name}`);
          return data.publicUrl;
        })
      );
      
      // Los detalles pueden venir con la orden o salir de la consulta de arriba.
      const details = order.details?.length
        ? order.details
        : order.orderDetails?.length
        ? order.orderDetails
        : orderFromDb?.orderDetails || [];

      // `updateOrder` necesita `prevOrder.details[0].orderDetailsId` para guardar.
      setPrevOrder((prev) => ({ ...prev, details }));

      const description = details?.[0]?.description || "";
      
      // Usar datos de la consulta directa si están disponibles
      const signatureData = orderFromDb?.clientSignaturePhoto || order.clientSignaturePhoto || null;
      const signatureName = orderFromDb?.clientSignatureName || order.clientSignatureName || "";
      
      
      setFormState({
        client: clientData,
        description: description,
        signature: signatureData,
        signature_aclaration: signatureName,
        pictures: files,
      });
      
    } catch (error) {
      console.error("Error en fetchOrderData:", error);
      ErrorModal({ 
        title: "Error al cargar la orden", 
        text: error.message,
        errorCode: "ORD002"
      });
    } finally {
      setIsLoading(false);
    }
  }, [isOperator, navigate]);

  useEffect(() => {
    if (location.state?.order) {
      fetchOrderData(location.state.order);
    }
    // Nueva orden: sin verificación inicial, se hará en el paso 2
  }, [location, fetchOrderData]);


  // Detección de cambios (universal)
  useEffect(() => {
    if (currentStep === 2) {
      const currentPicturesCount = formState.pictures.length;
      
      // Si es la primera vez que entramos al paso 2, establecer el estado inicial
      if (initialPicturesCount === 0) {
        setInitialPicturesCount(currentPicturesCount);
        setInitialPictures([...formState.pictures]);
        // setTempDeletedPictures([]);
        setShowActionButtons(false);
        
        // Verificar conexión cuando se entra al paso 2
        const checkStep2Connection = async () => {
          const hasConnection = await checkConnection();
          
          if (!hasConnection) {
            setIsOfflineMode(true);
          } else {
            setIsOfflineMode(false);
          }
        };
        
        checkStep2Connection();
      } else {
        // Si el conteo cambió desde el inicial, mostrar botones de acción
        if (currentPicturesCount !== initialPicturesCount) {
          setShowActionButtons(true);
        }
      }
    } else if (currentStep !== 2) {
      // Resetear estados cuando no estamos en el paso 2
      setShowActionButtons(false);
      setInitialPicturesCount(0);
      setInitialPictures([]);
      // setTempDeletedPictures([]);
    }
  }, [currentStep, formState.pictures.length, initialPicturesCount, formState.pictures]);

  // Vuelta desde el alta de cliente: se deja elegido y se sigue en el paso 1.
  useEffect(() => {
    const incoming = location.state?.selectedClient;
    if (!incoming) return;
    setFormState((prev) => ({ ...prev, client: incoming }));
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.selectedClient]);

  /**
   * Cada paso pide lo suyo: el 1 un cliente, el 2 la descripción del trabajo
   * (las fotos son opcionales) y el 3 cierra la orden.
   */
  const stepIsComplete = (step) => {
    if (step === 1) return Boolean(formState.client?.name || formState.client?.company);
    if (step === 2) return Boolean(formState.description);
    return true;
  };

  const canAdvance = currentStep !== 3 && stepIsComplete(currentStep);

  /**
   * Navegación por los pasos de arriba.
   *   · Hacia atrás: directo. Al salir del paso 3 hay que guardar la firma
   *     antes de desmontar el canvas; por eso el click al botón de guardado.
   *   · Hacia adelante: sólo al paso siguiente y pasando por `nextStep`, que es
   *     donde viven las validaciones y el guardado de cada paso.
   */
  const goToStep = (target) => {
    if (target === currentStep) return;

    if (target < currentStep) {
      if (currentStep === 3) {
      }
      setCurrentStep(Math.max(1, target));
      return;
    }

    // Siempre responde: si falta un dato, `nextStep` lo explica en vez de
    // dejar el click muerto sin ninguna señal.
    if (target === currentStep + 1) {
      nextStep();
    }
  };

  const prevStep = () => goToStep(currentStep - 1);

  // Función para manejar el botón cancelar en móvil (paso 2)
  const handleCancelPhotos = () => {
    if (isReadOnlyMode) return;
    
    // Restaurar el estado inicial de las imágenes
    setFormState(prev => ({
      ...prev,
      pictures: [...initialPictures]
    }));
    // Limpiar las imágenes marcadas para eliminación temporal
    setDeletedPictures([]);
    setShowActionButtons(false);
  };

  // Función para manejar el botón aceptar en móvil (paso 2)
  const handleAcceptPhotos = async () => {
    if (isReadOnlyMode) return;
    
    setIsUploadingImages(true);
    try {
      // Si estamos en modo edición, procesar cambios en el bucket
      if (isEdition) {
        const bucketId = prevOrder.bucketId || "orders";
        
        // 1. Eliminar imágenes marcadas para eliminación
        if (deletedPictures.length > 0 && prevOrder.orderId) {
          // Limpiar parámetros de URL si existen en los nombres de archivo y decodificar
          const cleanDeletedPictures = deletedPictures.map(name => decodeURIComponent(name.split('?')[0]));
          const deletedPaths = cleanDeletedPictures.map((name) => `${prevOrder.orderId}/${name}`);
          const { data: deleteData, error } = await supabase.storage
            .from(bucketId)
            .remove(deletedPaths);
          if (error) {
            throw new Error(`Error al eliminar imágenes: ${error.message}`);
          }
          // Limpiar la lista de imágenes eliminadas
          setDeletedPictures([]);
        }
        
        // 2. Subir imágenes nuevas al bucket
        const addedPictures = formState.pictures.filter(
          (item) => typeof item === 'object' && item?.isPictureAddedOnEdition && item.picture
        );
        if (addedPictures.length > 0 && prevOrder.orderId) {
          try {
            await uploadFiles(bucketId, addedPictures, prevOrder.orderId);
          } catch (uploadError) {
            // Manejar errores de imágenes específicos
            if (uploadError.message && uploadError.message.startsWith('SKIP_IMAGE:')) {
              const parts = uploadError.message.split(':');
              const fileName = parts[1];
              const reason = parts[2];
              console.warn(`Imagen omitida en actualización: ${fileName} - Razón: ${reason}`);
              // Continuar sin la imagen
            } else {
              throw uploadError; // Re-lanzar otros errores
            }
          }
        }
        
        // 3. Refrescar la lista de imágenes desde el bucket
        if (prevOrder.orderId) {
          const { data } = await supabase.storage.from(bucketId).list(`${prevOrder.orderId}/`);
          const files = await Promise.all(
            data.map(async (file) => {
              const { data } = supabase.storage
                .from(bucketId)
                .getPublicUrl(`${prevOrder.orderId}/${file.name}`);
              return data.publicUrl;
            })
          );
          setFormState(prev => ({
            ...prev,
            pictures: files.filter(url => typeof url === 'string')
          }));
        }
      }
      
      setShowActionButtons(false);
      // Actualizar el estado inicial para futuros cambios
      setInitialPictures([...formState.pictures]);
      setInitialPicturesCount(formState.pictures.length);
    } catch (error) {
      console.error("Error al aceptar cambios de fotos:", error);
      // Aquí podrías mostrar un mensaje de error al usuario
    } finally {
      setIsUploadingImages(false);
    }
  };


  const uploadFiles = useCallback(
    async (bucketName, files, orderId) => {
      if (!files || files.length === 0) {
        return true;
      }

      // Detectar si es Android para aplicar configuraciones específicas
      const isAndroid = /Android/i.test(navigator.userAgent);
      const isChrome = /Chrome/i.test(navigator.userAgent);
      const isAndroidChrome = isAndroid && isChrome;
      
      
      // Subidas: secuenciales en Android Chrome para reducir picos de memoria
      const processFile = async (file, index) => {
        const finalFile = file?.isPictureAddedOnEdition ? file.picture : file;
        
        // Validar archivo antes del procesamiento
        validateImageFile(finalFile);
        
        const filename = generateUniqueFileName(finalFile, orderId, index);
        
        // Validar tamaño de archivo solo si es extremadamente grande (más de 50MB)
        const fileSizeMB = finalFile.size / (1024 * 1024);
        if (fileSizeMB > 50) {
          throw new Error(`Archivo extremadamente grande: ${finalFile.name} (${fileSizeMB.toFixed(1)}MB). El tamaño máximo permitido es 50MB. Por favor, usa una imagen más pequeña.`);
        }
        
        // Comprimir imagen con reintentos (config específica Android más agresiva)
        const compressionOptions = isAndroidChrome ? {
          maxSizeMB: 1.2,
          maxWidthOrHeight: 1024,
          initialQuality: 0.65,
          useWebWorker: false,
        } : {
          maxSizeMB: 3,
          maxWidthOrHeight: maxWidth,
          initialQuality: quality,
        };
        
        let compressedFile = await compressImageWithRetry(finalFile, compressionOptions);
        
        // Configurar timeout específico para Android Chrome
        const timeout = isAndroidChrome ? 90000 : 30000;
        const controller = new AbortController();
          const timeoutId = setTimeout(() => {
            controller.abort();
          }, timeout);
        

        try {
          const { error } = await supabase.storage
            .from(bucketName)
            .upload(filename, compressedFile, {
              cacheControl: "3600",
              upsert: false,
            });
          clearTimeout(timeoutId);
          if (error) {
            throw new Error(`Error al subir archivo ${finalFile.name}: ${error.message}`);
          }
        } catch (uploadError) {
          clearTimeout(timeoutId);
          
          // Reintento con compresión 50% más agresiva antes de mostrar error
          
          // Calcular opciones 50% más agresivas
          const currentSize = compressionOptions.maxSizeMB || 1.2;
          const currentRes = compressionOptions.maxWidthOrHeight || 1024;
          const currentQuality = compressionOptions.initialQuality || 0.65;
          
          const ultraOptions = {
            maxSizeMB: currentSize * 0.5, // 50% más pequeño
            maxWidthOrHeight: Math.floor(currentRes * 0.7), // 70% de resolución
            initialQuality: Math.max(0.3, currentQuality * 0.7), // 70% de calidad (mínimo 30%)
            useWebWorker: false,
            alwaysKeepResolution: false,
            preserveExif: false,
          };
          
          
          try {
            compressedFile = await compressImageWithRetry(finalFile, ultraOptions, 2);
            
            const { error: errorRetry } = await supabase.storage
              .from(bucketName)
              .upload(filename, compressedFile, {
                cacheControl: "3600",
                upsert: false,
              });
              
            if (errorRetry) {
              throw new Error(`Reintento fallido al subir ${finalFile.name}: ${errorRetry.message}`);
            }
            
            return;
            
          } catch (retryErr) {
            
            // Último intento: usar archivo original si es muy pequeño
            if (finalFile.size < 500 * 1024) { // Menos de 500KB
              try {
                const { error: originalError } = await supabase.storage
                  .from(bucketName)
                  .upload(filename, finalFile, {
                    cacheControl: "3600",
                    upsert: false,
                  });
                  
                if (!originalError) {
                  return;
                }
              } catch (originalErr) {
              }
            }
            
            // Error final - continuar sin la imagen y avisar al usuario
            const message = retryErr?.message || uploadError?.message || '';
            const isConnectionError = message.includes("Failed to fetch") || retryErr?.name === "AbortError";
            
            // Verificar si el archivo es demasiado grande después de todos los intentos
            const fileSizeMB = finalFile.size / (1024 * 1024);
            let reason = 'error de procesamiento';
            
            if (fileSizeMB > 10) {
              reason = 'imagen demasiado grande, aún despues de redimensionarse (más de 10MB)';
            } else if (isConnectionError) {
              reason = 'conexión de internet débil';
            }
            
            console.warn(`No se pudo subir la imagen ${finalFile.name} (${fileSizeMB.toFixed(1)}MB) después de múltiples intentos. Continuando sin la imagen.`);
            
            // En lugar de lanzar error, continuar sin la imagen
            // El error se manejará a nivel superior para mostrar un mensaje informativo
            throw new Error(`SKIP_IMAGE:${finalFile.name}:${reason}`);
          }
        }
      };
      
      try {
        if (isAndroidChrome) {
          // Secuencial en Android Chrome
          for (let i = 0; i < files.length; i++) {
            await processFile(files[i], i);
          }
        } else {
          // Paralelo en otros dispositivos
          await Promise.all(files.map((f, i) => processFile(f, i)));
        }
        return true;
      } catch (error) {
        console.error(`Error en subida de archivos para orden ${orderId}:`, error);
        throw error;
      }
    },
    [maxWidth, quality]
  );

  const nextStep = async () => {
    try {
      // Guardar imágenes nuevas al salir del paso 2 en modo edición (solo si no está en modo de solo lectura)
      if (isEdition && currentStep === 2 && !isReadOnlyMode) {
        const addedPictures = formState.pictures.filter(
          (item) => typeof item === 'object' && item?.isPictureAddedOnEdition && item.picture
        );
        const bucketId = prevOrder.bucketId || "orders";
        if (addedPictures.length > 0 && prevOrder.orderId) {
          try {
            await uploadFiles(bucketId, addedPictures, prevOrder.orderId);
            // Limpiar el flag para que no se suban de nuevo al finalizar
            setFormState((prev) => ({
              ...prev,
              pictures: prev.pictures.map((pic) =>
                pic && typeof pic === 'object' && pic.isPictureAddedOnEdition
                  ? { ...pic, isPictureAddedOnEdition: false }
                  : pic
              ),
            }));
          } catch (uploadError) {
            // Manejar errores de imágenes específicos
            if (uploadError.message && uploadError.message.startsWith('SKIP_IMAGE:')) {
              const parts = uploadError.message.split(':');
              const fileName = parts[1];
              const reason = parts[2];
              console.warn(`Imagen omitida en actualización: ${fileName} - Razón: ${reason}`);
              // Continuar sin la imagen pero limpiar el flag
              setFormState((prev) => ({
                ...prev,
                pictures: prev.pictures.map((pic) =>
                  pic && typeof pic === 'object' && pic.isPictureAddedOnEdition
                    ? { ...pic, isPictureAddedOnEdition: false }
                    : pic
                ),
              }));
            } else {
              throw uploadError; // Re-lanzar otros errores
            }
          }
        }
      }
      if (currentStep !== 3 && stepIsComplete(currentStep)) {
        setCurrentStep((prevStep) => prevStep + 1);
        setMessage("");
      } else if (currentStep === 1) {
        setMessage("Debe escoger un cliente para continuar");
        ErrorModal({
          title: "Cliente no seleccionado",
          text: "Debe seleccionar un cliente para continuar con la orden",
          errorCode: "ORD003",
        });
      } else if (currentStep === 2) {
        setMessage("Debe especificar la descipción");
        ErrorModal({
          title: "Descripción faltante",
          text: "Debe especificar la descripción del trabajo a realizar",
          errorCode: "ORD004",
        });
      } else if (!formState.client?.name && !formState.client?.company) {
        // Guarda final: al pasar a validación por paso, el paso 3 quedaba sin
        // revisar cliente y descripción antes de guardar.
        setMessage("Debe escoger un cliente para continuar");
        ErrorModal({
          title: "Cliente no seleccionado",
          text: "Debe seleccionar un cliente para continuar con la orden",
          errorCode: "ORD003",
        });
      } else if (!formState.description) {
        setMessage("Debe especificar la descipción");
        ErrorModal({
          title: "Descripción faltante",
          text: "Debe especificar la descripción del trabajo a realizar",
          errorCode: "ORD004",
        });
      } else {
        // Solo permitir finalizar si no está en modo de solo lectura
        if (!isReadOnlyMode) {
          await handleSubmit();
        }
      }
    } catch (error) {
      console.error("Error en nextStep:", error);
      // Los errores ya son manejados en handleSubmit, createOrder y updateOrder
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      setIsFinished(true);
      
      
      if (!isEdition && !isOrderClosed) {
        await createOrder();
      } else if (isEdition && !isOrderClosed) {
        await updateOrder();
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Error en handleSubmit:", error);
      // Los errores ya son manejados en createOrder y updateOrder
    } finally {
      setIsLoading(false);
    }
  };

  // NOTA: El bucket 'orders' debe estar creado previamente en la consola de Supabase.
  const createOrder = async () => {
    setIsUploadingImages(true);
    try {
      // Validar que tenemos los datos necesarios
      if (!formState.client || !formState.client.clientId) {
        throw new Error("Datos del cliente incompletos o inválidos");
      }
      if (!formState.description || formState.description.trim() === "") {
        throw new Error("La descripción del trabajo es requerida");
      }
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();
      if (authError) {
        throw new Error("Error de autenticación: " + authError.message);
      }
      if (!user) {
        throw new Error("Usuario no autenticado");
      }
      const { data: employeeData, error: employeeError } = await supabase
        .from("employees")
        .select("employeeId")
        .eq("uid", user.id)
        .single();
      if (employeeError) {
        throw new Error(
          "Error al obtener el empleado: " + employeeError.message
        );
      }
      if (!employeeData) {
        throw new Error("No se encontró el empleado asociado al usuario");
      }
      const bucketName = "orders"; // Usar bucket fijo
      // 1. Crear la orden sin imágenes
      const wasSigned = formState.signature && formState.signature_aclaration;
      const hasImages = formState.pictures && formState.pictures.length > 0;
      
      // Determinar estado inicial: Solo TERMINADO si tiene firma Y imágenes
      // En modo offline (sin imágenes) siempre queda PENDIENTE
      let orderStatus = (wasSigned && hasImages) ? 2 : 1;
      
      
      const { data: orderData, error: orderError } = await supabase
        .from("orders")
        .insert([
          {
            status: orderStatus,
            clientId: formState.client.clientId,
            employeeId: employeeData.employeeId,
            ticketId: 1,
            bucketId: bucketName,
            clientSignatureName: formState.signature_aclaration,
            clientSignaturePhoto: formState.signature,
            currencyRate: 1,
          },
        ])
        .select("*");
      if (orderError) {
        throw orderError;
      }
      const orderId = orderData[0]?.orderId;
      if (!orderId) {
        throw new Error("No se pudo obtener el ID de la orden creada");
      }
      // 2. Subir imágenes a la subcarpeta de la orden
      let filesUploaded = true;
      let skippedImages = [];
      
      // Solo intentar subir imágenes si hay imágenes disponibles
      if (formState.pictures && formState.pictures.length > 0) {
        try {
          filesUploaded = await uploadFiles(bucketName, formState.pictures, orderId);
      } catch (uploadError) {
        // Manejar errores de imágenes específicos
        if (uploadError.message && uploadError.message.startsWith('SKIP_IMAGE:')) {
          const parts = uploadError.message.split(':');
          const fileName = parts[1];
          const reason = parts[2];
          skippedImages.push({ fileName, reason });
          filesUploaded = true; // Continuar sin la imagen
          console.warn(`Imagen omitida: ${fileName} - Razón: ${reason}`);
        } else {
          throw uploadError; // Re-lanzar otros errores
        }
      }
      }
      
      if (filesUploaded) {
        // 3. Ajustar estado de la orden si hubo imágenes que fallaron
        if (skippedImages.length > 0) {
          // Si hubo imágenes que fallaron, la orden debe quedar PENDIENTE
          orderStatus = 1; // PENDIENTE
          
          // Actualizar el estado de la orden en la base de datos
          const { error: updateStatusError } = await supabase
            .from("orders")
            .update({ status: orderStatus })
            .eq("orderId", orderId);
            
          if (updateStatusError) {
            console.warn("No se pudo actualizar el estado de la orden:", updateStatusError);
          }
        }
        
        // 4. Insertar el detalle de la orden
        const { error: detailsError } = await supabase
          .from("orderDetails")
          .insert([
            {
              orderId: orderId,
              quantity: 1,
              description: formState.description,
              price: 0,
              currency: 1,
              ivaPercentage: 21,
            },
          ]);
        if (detailsError) {
          throw detailsError;
        } else {
          // Mostrar mensaje sobre imágenes omitidas si las hay
          if (skippedImages.length > 0) {
            const imageNames = skippedImages.map(img => img.fileName).join(', ');
            const reasons = [...new Set(skippedImages.map(img => img.reason))];
            
            // Crear mensaje más descriptivo basado en las razones
            let reasonText = 'problemas técnicos';
            if (reasons.includes('imagen demasiado grande, aún despues de redimensionarse (más de 10MB)')) {
              reasonText = 'imágenes demasiado grandes (más de 10MB)';
            } else if (reasons.includes('conexión de internet débil')) {
              reasonText = 'conexión de internet débil';
            } else if (reasons.includes('error de procesamiento')) {
              reasonText = 'problemas de procesamiento';
            }
            
            // Mostrar modal de warning que permite continuar
            setTimeout(() => {
              // Usar SweetAlert2 para modal de warning
              Swal.fire({
                icon: 'warning',
                title: 'Imágenes No Subidas',
                html: `Algunas imágenes no se pudieron subir después de varios intentos de redimensionamiento debido a <strong>${reasonText}</strong>:<br><br><strong>${imageNames}</strong><br><br>La orden se creará en estado <strong>PENDIENTE</strong>. Puedes agregar estas imágenes más tarde editando la orden.`,
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#ff6b6b',
                allowOutsideClick: false,
                allowEscapeKey: false,
                showCancelButton: false,
                customClass: {
                  popup: 'swal-warning-popup',
                  title: 'swal-warning-title',
                  content: 'swal-warning-content'
                }
              }).then(() => {
                // Continuar con la navegación después de que el usuario presione "Continuar"
                navigate("/");
              });
            }, 1000);
          } else {
            // Si no hay imágenes omitidas, navegar directamente
            navigate("/");
          }
        }
      }
    } catch (error) {
      console.error("Error en createOrder:", error);
      
      // Usar el manejador de errores específico para imágenes si es un error de imagen
      const errorInfo = handleImageError(error);
      let errorMessage = errorInfo.userMessage;
      let errorNumber = 0;
      let technicalDetails = error.message || "Error desconocido";
      
      // Mapear tipos de error a números específicos
      if (errorInfo.type === 'validation') {
        errorNumber = 14;
      } else if (errorInfo.type === 'size') {
        errorNumber = 15;
      } else if (errorInfo.type === 'compression') {
        errorNumber = 9;
      } else if (error && typeof error === "object" && error !== null && error.message) {
        technicalDetails = error.message;
        if (error.message.includes("storage")) {
          errorMessage =
            "Error al subir las imágenes. Por favor, verifica que los archivos sean válidos y no excedan el tamaño máximo permitido.";
          errorNumber = 1;
        } else if (error.message.includes("bucket")) {
          errorMessage =
            "Error al crear el espacio de almacenamiento para las imágenes.";
          errorNumber = 2;
        } else if (error.message.includes("permission")) {
          errorMessage =
            "No tienes permisos suficientes para realizar esta acción.";
          errorNumber = 3;
        } else if (error.message.includes("network") || error.message.includes("Failed to fetch")) {
          // Detectar específicamente el error "Failed to fetch" que ocurre en Android Chrome
          const isAndroidChrome = /Android/i.test(navigator.userAgent) && /Chrome/i.test(navigator.userAgent);
          
          if (error.message.includes("Failed to fetch")) {
            // Extraer nombre de archivo del error si está disponible
            const fileMatch = error.message.match(/Archivo:\s*([^\s,().]+)/);
            const fileName = fileMatch ? fileMatch[1] : null;
            
            errorMessage = isAndroidChrome 
              ? (fileName 
                  ? `Error de conexión en Android Chrome con "${fileName}". Se intentó reducir automáticamente. Intenta con imagen más pequeña.`
                  : "Error de conexión específico de Android Chrome. Por favor, intenta con imágenes más pequeñas o verifica tu conexión a internet.")
              : (fileName
                  ? `Error de conexión al procesar "${fileName}". Verifica tu conexión a internet.`
                  : "Error de conexión de red. Por favor, verifica tu conexión a internet e intenta nuevamente.");
            technicalDetails = `${error.message} - Dispositivo: ${isAndroidChrome ? 'Android Chrome' : 'Otro'}`;
          } else {
            errorMessage = "Error de conexión. Por favor, verifica tu conexión a internet.";
            technicalDetails = error.message;
          }
          errorNumber = 4;
        } else if (error.message.includes("auth")) {
          errorMessage =
            "Error de autenticación. Por favor, verifica que hayas iniciado sesión correctamente.";
          errorNumber = 5;
        } else if (error.message.includes("employee")) {
          errorMessage =
            "Error al obtener los datos del empleado. Por favor, contacta al administrador.";
          errorNumber = 6;
        } else if (error.message.includes("client")) {
          errorMessage =
            "Error con los datos del cliente. Por favor, verifica que todos los datos del cliente sean correctos.";
          errorNumber = 7;
        } else if (error.message.includes("database")) {
          errorMessage =
            "Error al guardar los datos en la base de datos. Por favor, intenta nuevamente.";
          errorNumber = 8;
        } else if (error.message.includes("signature")) {
          errorMessage =
            "Error con la firma del cliente. Por favor, verifica que la firma se haya capturado correctamente.";
          errorNumber = 10;
        } else if (error.message.includes("validation")) {
          errorMessage =
            "Error de validación de datos. Por favor, verifica que todos los campos requeridos estén completos.";
          errorNumber = 11;
        } else if (error.message.includes("timeout")) {
          errorMessage =
            "La operación ha excedido el tiempo máximo de espera. Por favor, intenta nuevamente.";
          errorNumber = 12;
        } else if (error.message.includes("quota")) {
          errorMessage =
            "Se ha excedido la cuota de almacenamiento. Por favor, contacta al administrador.";
          errorNumber = 13;
        } else {
          errorMessage = "Error inesperado al crear la orden. Por favor, intenta nuevamente.";
          errorNumber = 0;
        }
      } else if (typeof error === "string") {
        technicalDetails = error;
        errorMessage = "Error inesperado al crear la orden. Por favor, intenta nuevamente.";
        errorNumber = 0;
      } else {
        technicalDetails = "Error sin detalles específicos";
        errorMessage = "Error inesperado al crear la orden. Por favor, intenta nuevamente.";
        errorNumber = 0;
      }
      
      ErrorModal({
        title: `Error ORD005: Error ${errorNumber} al crear la orden`,
        text: `${errorMessage}\n\nDetalles técnicos: ${technicalDetails}`,
        errorCode: "ORD005",
      });
    } finally {
      localStorage.removeItem("signatureData");
      setIsUploadingImages(false);
    }
  };

  const updateOrder = async () => {
    setIsUploadingImages(true);
    try {
      // Validación mejorada del cliente
      if (!formState.client) {
        throw new Error("No se encontraron datos del cliente en el formulario");
      }
      
      if (!formState.client.clientId) {
        throw new Error("El cliente no tiene un ID válido. Por favor, selecciona un cliente válido.");
      }
      
      // Verificar que el cliente tenga los campos básicos necesarios
      if (!formState.client.name && !formState.client.company) {
        throw new Error("Los datos del cliente están incompletos. Falta el nombre o la empresa.");
      }
      if (!formState.description || formState.description.trim() === "") {
        throw new Error("La descripción del trabajo es requerida");
      }
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();
      if (authError) {
        throw new Error("Error de autenticación: " + authError.message);
      }
      if (!user) {
        throw new Error("Usuario no autenticado");
      }
      if (deletedPictures.length) {
        const bucketId = prevOrder.bucketId || "orders";
        // Limpiar parámetros de URL si existen en los nombres de archivo y decodificar
        const cleanDeletedPictures = deletedPictures.map(name => decodeURIComponent(name.split('?')[0]));
        // Eliminar imágenes de la subcarpeta de la orden
        const deletedPaths = cleanDeletedPictures.map((name) => `${prevOrder.orderId}/${name}`);
        const { data: deleteData, error } = await supabase.storage
          .from(bucketId)
          .remove(deletedPaths);
        if (error) {
          throw error;
        }
      }
      const addedPictures = formState.pictures.filter(
        (item) => typeof item === 'object' && item?.isPictureAddedOnEdition && item.picture
      );
      const bucketId = prevOrder.bucketId || "orders";
      if (addedPictures.length > 0 && !bucketId) {
        throw new Error(
          "No se encontró el bucket de almacenamiento de la orden"
        );
      }
      // Subir imágenes nuevas a la subcarpeta de la orden
      
      const filesUploaded = await uploadFiles(
        bucketId,
        addedPictures,
        prevOrder.orderId
      );
      
      
      if (filesUploaded) {
        const wasSigned = formState.signature && formState.signature_aclaration;
        
        // Verificar imágenes tanto en formState como en storage
        const formStateImages = formState.pictures && formState.pictures.length > 0;
        
        // Verificar imágenes en storage
        let storageImages = false;
        try {
          const { data: storageFiles } = await supabase.storage
            .from(prevOrder.bucketId || "orders")
            .list(`${prevOrder.orderId}/`);
          storageImages = storageFiles && storageFiles.length > 0;
        } catch (error) {
          console.error("Error verificando imágenes en storage:", error);
        }
        
        const hasImages = formStateImages || storageImages;
        
        // Determinar estado: Solo TERMINADO si tiene firma Y imágenes
        // Sin imágenes siempre queda PENDIENTE
        const finalStatus = (wasSigned && hasImages) 
          ? orderObjectStatus.terminado 
          : orderObjectStatus.pendiente;
        
        
        const { error: orderError } = await supabase
          .from("orders")
          .update({
            status: finalStatus,
            clientId: formState.client.clientId,
            clientSignatureName: formState.signature_aclaration,
            clientSignaturePhoto: formState.signature,
          })
          .eq("orderId", prevOrder.orderId)
          .select("*");
        if (orderError) {
          throw orderError;
        }
        if (
          !prevOrder.details ||
          !prevOrder.details[0] ||
          !prevOrder.details[0].orderDetailsId
        ) {
          throw new Error(
            "No se encontraron los detalles de la orden para actualizar"
          );
        }
        if (!prevOrder.orderId) {
          throw new Error("No se encontró el ID de la orden para actualizar");
        }
        // Obtener los valores actuales de price e ivaPercentage
        const currentPrice = prevOrder.details?.[0]?.price;
        const currentIvaPercentage = prevOrder.details?.[0]?.ivaPercentage;
        
        // Solo actualizar price e ivaPercentage si no tienen valores previos (null, undefined, o 0)
        const updateData = {
          description: formState.description,
          currency: 1,
        };
        
        // Solo establecer price: 0 si no existe un valor previo
        if (currentPrice === null || currentPrice === undefined) {
          updateData.price = 0;
        }
        
        // Solo establecer ivaPercentage: 21 si no existe un valor previo
        if (currentIvaPercentage === null || currentIvaPercentage === undefined) {
          updateData.ivaPercentage = 21;
        }
        
        const { error: detailsError } = await supabase
          .from("orderDetails")
          .update(updateData)
          .eq("orderDetailsId", prevOrder.details[0].orderDetailsId);
        if (detailsError) {
          throw detailsError;
        } else {
          navigate("/");
        }
      }
    } catch (error) {
      console.error("Error en updateOrder:", error);
      
      // Usar el manejador de errores específico para imágenes si es un error de imagen
      const errorInfo = handleImageError(error);
      let errorMessage = errorInfo.userMessage;
      let errorNumber = 0;
      let technicalDetails = error.message || "Error desconocido";
      
      // Mapear tipos de error específicos para actualización
      if (errorInfo.type === 'validation') {
        errorNumber = 14;
      } else if (errorInfo.type === 'size') {
        errorNumber = 15;
      } else if (errorInfo.type === 'compression') {
        errorNumber = 9;
      } else if (error && typeof error === "object" && error !== null && error.message) {
        technicalDetails = error.message;
        
        // Errores específicos de actualización
        if (error.message.includes("No se encontraron datos del cliente")) {
          errorMessage = "Error: No se encontraron datos del cliente. Por favor, recarga la página e intenta nuevamente.";
          errorNumber = 16;
        } else if (error.message.includes("El cliente no tiene un ID válido")) {
          errorMessage = "Error: El cliente seleccionado no es válido. Por favor, selecciona un cliente diferente.";
          errorNumber = 17;
        } else if (error.message.includes("Los datos del cliente están incompletos")) {
          errorMessage = "Error: Los datos del cliente están incompletos. Por favor, verifica que el cliente tenga nombre y empresa.";
          errorNumber = 18;
        } else if (error.message.includes("No se encontró el cliente asociado")) {
          errorMessage = "Error: No se encontró el cliente asociado a esta orden. Por favor, contacta al administrador.";
          errorNumber = 19;
        } else if (error.message.includes("storage")) {
          errorMessage = "Error al subir las imágenes. Por favor, verifica que los archivos sean válidos y no excedan el tamaño máximo permitido.";
          errorNumber = 1;
        } else if (error.message.includes("bucket")) {
          errorMessage = "Error al acceder al espacio de almacenamiento de las imágenes.";
          errorNumber = 2;
        } else if (error.message.includes("permission")) {
          errorMessage = "No tienes permisos suficientes para realizar esta acción.";
          errorNumber = 3;
        } else if (error.message.includes("network") || error.message.includes("Failed to fetch")) {
          // Detectar específicamente el error "Failed to fetch" que ocurre en Android Chrome
          const isAndroidChrome = /Android/i.test(navigator.userAgent) && /Chrome/i.test(navigator.userAgent);
          
          if (error.message.includes("Failed to fetch")) {
            // Extraer nombre de archivo del error si está disponible
            const fileMatch = error.message.match(/Archivo:\s*([^\s,().]+)/);
            const fileName = fileMatch ? fileMatch[1] : null;
            
            errorMessage = isAndroidChrome 
              ? (fileName 
                  ? `Error de conexión en Android Chrome con "${fileName}". Se intentó reducir automáticamente. Intenta con imagen más pequeña.`
                  : "Error de conexión específico de Android Chrome. Por favor, intenta con imágenes más pequeñas o verifica tu conexión a internet.")
              : (fileName
                  ? `Error de conexión al procesar "${fileName}". Verifica tu conexión a internet.`
                  : "Error de conexión de red. Por favor, verifica tu conexión a internet e intenta nuevamente.");
            technicalDetails = `${error.message} - Dispositivo: ${isAndroidChrome ? 'Android Chrome' : 'Otro'}`;
          } else {
            errorMessage = "Error de conexión. Por favor, verifica tu conexión a internet.";
            technicalDetails = error.message;
          }
          errorNumber = 4;
        } else if (error.message.includes("auth")) {
          errorMessage = "Error de autenticación. Por favor, verifica que hayas iniciado sesión correctamente.";
          errorNumber = 5;
        } else if (error.message.includes("database")) {
          errorMessage = "Error al guardar los datos en la base de datos. Por favor, intenta nuevamente.";
          errorNumber = 8;
        } else if (error.message.includes("signature")) {
          errorMessage = "Error con la firma del cliente. Por favor, verifica que la firma se haya capturado correctamente.";
          errorNumber = 10;
        } else if (error.message.includes("validation")) {
          errorMessage = "Error de validación de datos. Por favor, verifica que todos los campos requeridos estén completos.";
          errorNumber = 11;
        } else if (error.message.includes("timeout")) {
          errorMessage = "La operación ha excedido el tiempo máximo de espera. Por favor, intenta nuevamente.";
          errorNumber = 12;
        } else if (error.message.includes("quota")) {
          errorMessage = "Se ha excedido la cuota de almacenamiento. Por favor, contacta al administrador.";
          errorNumber = 13;
        } else {
          errorMessage = "Error inesperado al actualizar la orden. Por favor, intenta nuevamente.";
          errorNumber = 0;
        }
      } else if (typeof error === "string") {
        technicalDetails = error;
        errorMessage = "Error inesperado al actualizar la orden. Por favor, intenta nuevamente.";
        errorNumber = 0;
      } else {
        technicalDetails = "Error sin detalles específicos";
        errorMessage = "Error inesperado al actualizar la orden. Por favor, intenta nuevamente.";
        errorNumber = 0;
      }
      
      ErrorModal({
        title: `Error ORD002: Error ${errorNumber} al actualizar la orden`,
        text: `${errorMessage}\n\nDetalles técnicos: ${technicalDetails}`,
        errorCode: "ORD002",
      });
    } finally {
      localStorage.removeItem("signatureData");
      setIsUploadingImages(false);
    }
  };

  return isLoading ? (
    <LoaderSpinner />
  ) : (
    <>
      {/* Estilos para el modal de warning */}
      <style>
        {`
          .swal-warning-popup {
            border-radius: 12px !important;
            box-shadow: 0 8px 32px rgba(0,0,0,0.2) !important;
          }
          .swal-warning-title {
            color: #f39c12 !important;
            font-weight: 600 !important;
            font-size: 20px !important;
          }
          .swal-warning-content {
            color: #2c3e50 !important;
            font-size: 14px !important;
            line-height: 1.5 !important;
          }
          .swal2-warning {
            border-color: #f39c12 !important;
            color: #f39c12 !important;
          }
        `}
      </style>
      <Box
        className="order_form_container"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          width: "100%",
          maxWidth: "100%",
          alignItems: "stretch",
          // El scroll es el del documento: el scroller interno que había antes
          // dejaba la barra de acciones detrás de la barra inferior en teléfono.
          paddingBottom: { xs: "8px", md: 0 },
        }}
      >
        {isReadOnlyMode && (
          <Box style={{ 
            textAlign: 'center', 
            padding: '10px',
            backgroundColor: '#fff3cd',
            borderRadius: '8px',
            border: '1px solid #ffeaa7',
            marginBottom: '20px'
          }}>
            <p style={{ 
              margin: 0, 
              color: '#856404',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Esta orden está terminada.
            </p>
          </Box>
        )}
        <StepHeader
          currentStep={currentStep}
          onStepClick={goToStep}
          canAdvance={canAdvance}
          subtitleFor={(step, state) => {
            if (step === 1) {
              return formState.client?.company || formState.client?.name || "Sin elegir";
            }
            if (step === 2) {
              if (state === "pending") return "Pendiente";
              const fotos = (formState.pictures || []).length;
              return formState.description
                ? `${fotos} ${fotos === 1 ? "foto" : "fotos"}`
                : "Sin descripción";
            }
            return formState.signature ? "Firmada" : state === "active" ? "En edición" : "Pendiente";
          }}
        />

        {currentStep === 1 && (
          <ClientPicker
            formState={formState}
            setFormState={setFormState}
            isReadOnlyMode={isReadOnlyMode}
          />
        )}
        {currentStep === 2 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 360px" },
              gap: "16px",
              alignItems: "stretch",
              width: "100%",
            }}
          >
            <WorkDescription
              isOrderClosed={isOrderClosed}
              formState={formState}
              setFormState={setFormState}
              isReadOnlyMode={isReadOnlyMode}
            />

            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 }}>
              <Photos
                panel={!isMobile}
                hideClientInfo
                isOrderClosed={isOrderClosed}
                formState={formState}
                setFormState={setFormState}
                setDeletedPictures={setDeletedPictures}
                orderStatus={prevOrder.status}
                isEdition={isEdition}
                isReadOnlyMode={isReadOnlyMode}
                isOfflineMode={isOfflineMode}
              />
              <Box sx={{ display: { xs: "none", lg: "block" } }}>
                <OrderChecklist formState={formState} />
              </Box>
            </Box>
          </Box>
        )}
        {currentStep === 3 && (
          <Signature
            isEdition={isEdition}
            isOrderClosed={isOrderClosed}
            formState={formState}
            isFinished={isFinished}
            setFormState={setFormState}
            isReadOnlyMode={isReadOnlyMode}
          />
        )}
        {message && <p style={{ margin: 0, color: colors.red }}>{message}</p>}
      {/*
        Barra de acciones pegada abajo: antes quedaba debajo del pliegue y había
        que scrollear en cada paso para encontrar "Siguiente".
      */}
      <Box
        sx={{
          position: "sticky",
          // En teléfono la barra inferior es fija: la de acciones se apoya arriba.
          bottom: {
            xs: "calc(68px + env(safe-area-inset-bottom))",
            md: 0,
          },
          zIndex: 5,
          width: "100%",
          flexShrink: 0,
          marginTop: "auto",
          paddingTop: "12px",
          paddingBottom: { xs: "12px", md: "16px" },
          boxSizing: "border-box",
          backgroundColor: tokens.canvas,
          borderTop: `1px solid ${tokens.border}`,
        }}
      >
        {currentStep === 2 && showActionButtons && !isReadOnlyMode && !isOfflineMode ? (
          // Botones de acción para paso 2 cuando hay cambios
          <Box style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <RedButton
              title="Cancelar"
              onClick={handleCancelPhotos}
              style={{
                backgroundColor: colors.secondaryGray,
                flex: 1,
                maxWidth: '140px',
                margin: '8px 4px',
              }}
            />
            <RedButton
              title="Aceptar"
              onClick={handleAcceptPhotos}
              style={{
                backgroundColor: colors.red,
                flex: 1,
                maxWidth: '140px',
                margin: '8px 4px',
              }}
            />
          </Box>
        ) : (
          // Atrás + acción primaria. El "Atrás" reemplaza a la flecha del encabezado.
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
            {currentStep > 1 && !(currentStep === 2 && showActionButtons) && (
              <Button
                variant="outlined"
                onClick={prevStep}
                sx={{ minHeight: 48, minWidth: 100, flexShrink: 0 }}
              >
                Atrás
              </Button>
            )}
            {/* En escritorio la acción va a la derecha; en teléfono, a todo el ancho. */}
            <Box sx={{ display: { xs: "none", md: "block" }, flexGrow: 1 }} />
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disableElevation
              onClick={isReadOnlyMode && currentStep === 3 ? () => navigate("/") : nextStep}
              sx={{
                minHeight: 48,
                flexGrow: { xs: 1, md: 0 },
                minWidth: { md: 240 },
                fontSize: "15px",
              }}
            >
              {isReadOnlyMode
                ? currentStep === 3
                  ? "Volver"
                  : "Siguiente"
                : currentStep === 3
                ? "Finalizar orden"
                : "Siguiente"}
            </Button>
          </Box>
        )}
      </Box>
      
      {/* Botón Siguiente flotante SOLO para modo offline en paso 2 */}
      {currentStep === 2 && isOfflineMode && formState.client?.name && formState.description && (
        <Box style={{ 
          position: "fixed",
          bottom: "320px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "90%",
          maxWidth: "300px",
          zIndex: 1000
        }}>
          <RedButton
            title="Siguiente"
            type="button"
            onClick={() => {
              nextStep();
            }}
            style={{
              backgroundColor: colors.red,
              width: "100%",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)"
            }}
          />
        </Box>
      )}
      
      {isUploadingImages && <FullScreenLoader message="Subiendo imágenes..." />}
      </Box>
    </>
  );
};

export default ServiceOrders;
